/**
 * The dashboards page header: a fixed title, the current scope beside it, and
 * the page's actions (BON-534, generalised in BON-538).
 *
 * Mirrors the design's `page-header.jsx` — a CONSTANT title with the scope worn
 * as an eyebrow next to it, rather than the region name worn as the heading, so
 * someone landing deep in the hierarchy is still told which page they are on.
 *
 * Two alignments, nested, which is the whole reason this is a component rather
 * than two copies. The title and the scope share a BASELINE: the scope is set
 * in running text and reads as a continuation of the title, so centring it
 * against a 30px heading leaves it sitting visibly high. The actions are a
 * separate row item, CENTRED, because a button has no text baseline worth
 * matching and baseline-aligning one against the heading drops it below the
 * line. Flattening the two into a single flex row forces one rule on both, and
 * either choice is wrong for half the row.
 *
 * The title is ALWAYS rendered, at every width. The phone bar above it carries
 * the brand, not the page name (`NebariMobileBar`), so a page that hid its own
 * heading on a phone would have none at all.
 *
 * `DashboardsPage` renders this slot FULL-BLEED, outside its own padded area,
 * so the page padding lives here. The values are the design's own per-device
 * padding from `overview-shell.jsx`.
 */
import type { ReactNode } from 'react';

export interface DashboardPageHeaderProps {
  /** The page's name. Constant at every scope. */
  title: string;
  /** The scope control, worn beside the title. */
  scope?: ReactNode;
  /** Right-aligned page actions. */
  actions?: ReactNode;
  /** Rendered under the title row — a breadcrumb, usually. */
  below?: ReactNode;
  isPhone: boolean;
}

/** The design's page padding (`overview-shell.jsx`). */
const PAGE = {
  phone: { padding: '12px 16px 0', gap: 12, title: 20 },
  desktop: { padding: '18px 32px 0', gap: 14, title: 30 },
} as const;

export function DashboardPageHeader({
  title,
  scope,
  actions,
  below,
  isPhone,
}: DashboardPageHeaderProps) {
  const size = isPhone ? PAGE.phone : PAGE.desktop;

  return (
    <header style={{ padding: size.padding, paddingBottom: size.gap }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isPhone ? 10 : 14,
          minWidth: 0,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: isPhone ? 10 : 14,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <h1
            className="nb-display"
            style={{
              margin: 0,
              fontSize: size.title,
              color: 'var(--nb-bark-dark)',
              letterSpacing: '.01em',
              lineHeight: 1,
            }}
          >
            {title}
          </h1>
          {scope}
        </div>
        {actions && <div style={{ marginLeft: 'auto' }}>{actions}</div>}
      </div>
      {below && <div style={{ marginTop: isPhone ? 8 : 10 }}>{below}</div>}
    </header>
  );
}
